import { PageLayout } from '@/components/layout/PageLayout';
import { Helmet } from 'react-helmet-async';
import { SEO } from '@/components/shared/SEO';
import { Breadcrumb, useBreadcrumbs } from '@/components/ui/Breadcrumb';
import { useTenant } from '@/hooks/useTenant';
import {
  Check,
  Eye,
  Keyboard,
  Mouse,
  Contrast,
  AlertCircle,
  Mail,
  Phone,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';

export default function AccesibilidadPage() {
  const navigate = useNavigate();
  const { config } = useTenant();

  // Generate URLs and data
  const currentUrl = `${window.location.origin}/accesibilidad`;

  // JSON-LD Structured Data
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    '@id': `${currentUrl}#webpage`,
    url: currentUrl,
    name: 'Declaración de Accesibilidad',
    description:
      'Nuestro compromiso con la accesibilidad web y el cumplimiento de las pautas WCAG 2.1 AA',
    breadcrumb: {
      '@type': 'BreadcrumbList',
      itemListElement: [
        {
          '@type': 'ListItem',
          position: 1,
          name: 'Inicio',
          item: window.location.origin,
        },
        {
          '@type': 'ListItem',
          position: 2,
          name: 'Accesibilidad',
          item: currentUrl,
        },
      ],
    },
  };

  // Generate breadcrumb items
  const breadcrumbItems = useBreadcrumbs([
    {
      label: 'Accesibilidad',
      translationKey: 'nav.accessibility',
    },
  ]);

  const accessibilityFeatures = [
    {
      icon: Keyboard,
      title: 'Navegación por Teclado',
      description:
        'Todos los elementos interactivos son accesibles mediante el teclado usando las teclas Tab, Enter y Escape.',
    },
    {
      icon: Eye,
      title: 'Soporte para Lectores de Pantalla',
      description:
        'Implementamos etiquetas ARIA y texto alternativo en imágenes para garantizar la compatibilidad con lectores de pantalla.',
    },
    {
      icon: Contrast,
      title: 'Contraste de Color',
      description:
        'Todos los textos cumplen con las proporciones de contraste WCAG 2.1 AA (4.5:1 para texto normal, 3:1 para texto grande).',
    },
    {
      icon: Mouse,
      title: 'Tamaños de Área Táctil',
      description:
        'Los botones y enlaces tienen un tamaño mínimo de 44x44 píxeles para facilitar la interacción táctil.',
    },
  ];

  const knownLimitations = [
    {
      issue: 'Imágenes de Terceros',
      description:
        'Algunas imágenes cargadas por terceros pueden carecer de texto alternativo apropiado.',
      status: 'En progreso',
    },
    {
      issue: 'Contenido Multimedia',
      description:
        'Videos incrustados de plataformas externas pueden no incluir subtítulos o transcripciones.',
      status: 'Planificado',
    },
    {
      issue: 'Documentos PDF',
      description:
        'Algunos documentos PDF antiguos pueden no estar completamente optimizados para lectores de pantalla.',
      status: 'En revisión',
    },
  ];

  return (
    <>
      <SEO
        title="Declaración de Accesibilidad"
        description="Nuestro compromiso con la accesibilidad web según las pautas WCAG 2.1 AA. Conozca nuestras características de accesibilidad y cómo reportar problemas."
        keywords={[
          'accesibilidad web',
          'WCAG 2.1',
          'accesibilidad AA',
          'navegación por teclado',
          'lectores de pantalla',
          'contraste de color',
        ]}
        canonicalPath="/accesibilidad"
        ogImage="/images/hero/accessibility-hero.jpg"
      />

      <Helmet>
        {/* JSON-LD */}
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <PageLayout
        hero={{
          title: 'Declaración de Accesibilidad',
          subtitle: 'Nuestro compromiso con una web accesible para todos',
          backgroundImage: '/images/hero/accessibility-hero.jpg',
          showSearch: false,
          showCTA: false,
        }}
      >
        {/* Breadcrumb */}
        <div className="container mx-auto px-4 pt-6">
          <Breadcrumb items={breadcrumbItems} />
        </div>

        {/* Commitment Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Nuestro Compromiso
                </h2>
                <p className="text-xl text-gray-600">
                  Estamos comprometidos a garantizar la accesibilidad digital
                  para personas con discapacidades
                </p>
              </div>

              <div className="prose prose-lg max-w-none text-gray-700">
                <p className="text-lg leading-relaxed mb-6">
                  En <strong>{config?.name || 'nuestra empresa'}</strong>,
                  creemos que todos deberían tener acceso a la información y los
                  servicios en línea, independientemente de sus capacidades. Nos
                  esforzamos por cumplir con las{' '}
                  <strong>
                    Pautas de Accesibilidad al Contenido Web (WCAG) 2.1 nivel
                    AA
                  </strong>
                  , reconocidas internacionalmente como el estándar para la
                  accesibilidad web.
                </p>

                <p className="text-lg leading-relaxed mb-6">
                  Este compromiso abarca todas las áreas de nuestro sitio web y
                  se aplica a:
                </p>

                <ul className="space-y-2 mb-8">
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span>Navegación intuitiva y consistente</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span>Contenido comprensible y bien estructurado</span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span>
                      Compatibilidad con tecnologías de asistencia
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-6 w-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                    <span>
                      Diseño responsive adaptable a diferentes dispositivos
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Accessibility Features */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Características de Accesibilidad
              </h2>
              <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                Implementamos múltiples características para garantizar una
                experiencia accesible
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              {accessibilityFeatures.map((feature, index) => {
                const IconComponent = feature.icon;
                return (
                  <div
                    key={index}
                    className="bg-white rounded-xl shadow-md p-6 hover:shadow-lg transition-shadow"
                  >
                    <div className="flex items-start">
                      <div
                        className="w-12 h-12 rounded-full flex items-center justify-center text-white mr-4 flex-shrink-0"
                        style={{ backgroundColor: config?.primaryColor }}
                      >
                        <IconComponent className="h-6 w-6" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-gray-900 mb-2">
                          {feature.title}
                        </h3>
                        <p className="text-gray-600">{feature.description}</p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* WCAG Compliance */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Cumplimiento WCAG 2.1
                </h2>
                <p className="text-xl text-gray-600">
                  Nuestro sitio web cumple con el nivel AA de las pautas WCAG
                  2.1
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-8">
                <div className="flex items-start">
                  <AlertCircle className="h-6 w-6 text-blue-600 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <h3 className="text-lg font-semibold text-blue-900 mb-2">
                      ¿Qué es WCAG 2.1 AA?
                    </h3>
                    <p className="text-blue-800">
                      Las Pautas de Accesibilidad al Contenido Web (WCAG) 2.1
                      son estándares internacionales desarrollados por el World
                      Wide Web Consortium (W3C). El nivel AA es el nivel de
                      conformidad recomendado que cubre una amplia gama de
                      necesidades de accesibilidad.
                    </p>
                  </div>
                </div>
              </div>

              <div className="grid md:grid-cols-4 gap-6 mb-8">
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3"
                    style={{ backgroundColor: config?.primaryColor }}
                  >
                    P
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Perceptible
                  </h4>
                  <p className="text-sm text-gray-600">
                    La información debe ser presentable para todos los usuarios
                  </p>
                </div>
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3"
                    style={{ backgroundColor: config?.primaryColor }}
                  >
                    O
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Operable
                  </h4>
                  <p className="text-sm text-gray-600">
                    La interfaz debe ser navegable de múltiples formas
                  </p>
                </div>
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3"
                    style={{ backgroundColor: config?.primaryColor }}
                  >
                    C
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-1">
                    Comprensible
                  </h4>
                  <p className="text-sm text-gray-600">
                    El contenido debe ser fácil de entender
                  </p>
                </div>
                <div className="text-center">
                  <div
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-3"
                    style={{ backgroundColor: config?.primaryColor }}
                  >
                    R
                  </div>
                  <h4 className="font-semibold text-gray-900 mb-1">Robusto</h4>
                  <p className="text-sm text-gray-600">
                    Compatible con tecnologías actuales y futuras
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Known Limitations */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Limitaciones Conocidas
                </h2>
                <p className="text-xl text-gray-600">
                  Somos transparentes sobre las áreas en las que seguimos
                  trabajando
                </p>
              </div>

              <div className="space-y-4">
                {knownLimitations.map((limitation, index) => (
                  <div
                    key={index}
                    className="bg-white border border-gray-200 rounded-lg p-6"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h3 className="text-lg font-semibold text-gray-900 mb-2">
                          {limitation.issue}
                        </h3>
                        <p className="text-gray-600 mb-3">
                          {limitation.description}
                        </p>
                      </div>
                      <span
                        className="px-3 py-1 rounded-full text-sm font-medium ml-4"
                        style={{
                          backgroundColor:
                            limitation.status === 'En progreso'
                              ? '#FEF3C7'
                              : limitation.status === 'Planificado'
                              ? '#DBEAFE'
                              : '#FEE2E2',
                          color:
                            limitation.status === 'En progreso'
                              ? '#92400E'
                              : limitation.status === 'Planificado'
                              ? '#1E3A8A'
                              : '#991B1B',
                        }}
                      >
                        {limitation.status}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
                <p className="text-yellow-800">
                  <strong>Nota:</strong> Estamos trabajando activamente para
                  resolver estas limitaciones. Si encuentra algún problema de
                  accesibilidad, por favor háganoslo saber.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Feedback Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Mecanismo de Retroalimentación
                </h2>
                <p className="text-xl text-gray-600">
                  Su opinión nos ayuda a mejorar continuamente
                </p>
              </div>

              <div className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-8">
                <p className="text-lg text-gray-700 mb-6 text-center">
                  Si encuentra barreras de accesibilidad en nuestro sitio web o
                  tiene sugerencias sobre cómo podemos mejorar, nos encantaría
                  escuchar de usted.
                </p>

                <div className="grid md:grid-cols-2 gap-6 mb-8">
                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex items-center mb-3">
                      <Mail className="h-5 w-5 text-gray-600 mr-2" />
                      <h4 className="font-semibold text-gray-900">
                        Por Correo Electrónico
                      </h4>
                    </div>
                    <a
                      href={`mailto:${config?.contact.email}?subject=Reporte de Accesibilidad`}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {config?.contact.email}
                    </a>
                    <p className="text-sm text-gray-600 mt-2">
                      Asunto: "Reporte de Accesibilidad"
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-6 shadow-sm">
                    <div className="flex items-center mb-3">
                      <Phone className="h-5 w-5 text-gray-600 mr-2" />
                      <h4 className="font-semibold text-gray-900">
                        Por Teléfono
                      </h4>
                    </div>
                    <a
                      href={`tel:${config?.contact.phone}`}
                      className="text-blue-600 hover:text-blue-800 underline"
                    >
                      {config?.contact.phone}
                    </a>
                    <p className="text-sm text-gray-600 mt-2">
                      Horario: Lunes a Viernes, 9:00 - 18:00
                    </p>
                  </div>
                </div>

                <div className="text-center">
                  <p className="text-gray-600 mb-4">
                    También puede utilizar nuestro formulario de contacto
                  </p>
                  <Button
                    size="lg"
                    onClick={() => navigate('/contacto')}
                    style={{ backgroundColor: config?.primaryColor }}
                    className="text-white hover:opacity-90"
                  >
                    Ir al Formulario de Contacto
                  </Button>
                </div>
              </div>

              <div className="mt-8">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">
                  Qué incluir en su reporte:
                </h3>
                <ul className="space-y-2 text-gray-700">
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span>
                      <strong>URL de la página</strong> donde encontró el
                      problema
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span>
                      <strong>Descripción del problema</strong> con el mayor
                      detalle posible
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span>
                      <strong>Navegador y tecnología de asistencia</strong> que
                      está utilizando
                    </span>
                  </li>
                  <li className="flex items-start">
                    <Check className="h-5 w-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span>
                      <strong>Capturas de pantalla</strong> si es posible
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* External Resources */}
        <section className="py-16 bg-gray-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="text-center mb-12">
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                  Recursos de Accesibilidad
                </h2>
                <p className="text-xl text-gray-600">
                  Aprenda más sobre accesibilidad web
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-6">
                <a
                  href="https://www.w3.org/WAI/WCAG21/quickref/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow group"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600">
                        Guía Rápida WCAG 2.1
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Referencia completa de las pautas WCAG 2.1
                      </p>
                    </div>
                    <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
                  </div>
                </a>

                <a
                  href="https://www.w3.org/WAI/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow group"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600">
                        Iniciativa de Accesibilidad Web (WAI)
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Recursos y estrategias de accesibilidad del W3C
                      </p>
                    </div>
                    <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
                  </div>
                </a>

                <a
                  href="https://www.a11yproject.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow group"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600">
                        The A11Y Project
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Guías prácticas y recursos de accesibilidad
                      </p>
                    </div>
                    <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
                  </div>
                </a>

                <a
                  href="https://webaim.org/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white border border-gray-200 rounded-lg p-6 hover:shadow-lg transition-shadow group"
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-2 group-hover:text-blue-600">
                        WebAIM
                      </h3>
                      <p className="text-gray-600 text-sm">
                        Evaluación y mejora de accesibilidad web
                      </p>
                    </div>
                    <ExternalLink className="h-5 w-5 text-gray-400 group-hover:text-blue-600" />
                  </div>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Last Updated */}
        <section className="py-8 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-sm text-gray-500">
                <strong>Última actualización:</strong>{' '}
                {new Date().toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                })}
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Esta declaración de accesibilidad se revisa y actualiza
                periódicamente para reflejar nuestros esfuerzos continuos.
              </p>
            </div>
          </div>
        </section>
      </PageLayout>
    </>
  );
}
